import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import fdaApi from '../../../../api/data-sections/fdaApi';
import { useSystemError } from '../../../Error/SystemErrorContext';
import { FdaEventModel } from '../../../../models/data-sections/fda/eventModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface FdaEventModalProps { searchExecutionId: string; selected_event: FdaEventModel; }

const FdaEventModal: React.FC<FdaEventModalProps> = ({ searchExecutionId, selected_event }) => {
    const [eventData, setEventData] = useState<FdaEventModel | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didErrorOccur, setDidErrorOccur] = useState(false);
    const { triggerSystemError } = useSystemError();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await fetchApiGetEventDetails(searchExecutionId, selected_event.mdr_report_key, new Date(selected_event.__ci_system_search_date_key ?? 0));
            } catch (error) {
                console.error('Error during fetch:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [searchExecutionId, selected_event]);

    const fetchApiGetEventDetails = async (search_execution_id: string, mdr_report_key: string, __ci_system_search_date_key: Date): Promise<void> => {
        try {
            const response = await fdaApi.getEventDetail(search_execution_id, mdr_report_key, __ci_system_search_date_key);
            if (response.success) setEventData((response as ResponseSuccessModel).data?.[0] || null);
            else handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
        } catch (error) {
            handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                <Typography variant="h6">{selected_event.manufacturer_g1_name}</Typography>
                <Typography variant="subtitle2" color="textSecondary">MDR Key: {selected_event.mdr_report_key}</Typography>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            ) : didErrorOccur || !eventData ? (
                <Typography color="error">Failed to load Event details.</Typography>
            ) : (
                <Box display="flex" flexDirection="column" gap={2} mt={2}>
                    <Typography><strong>Event Type:</strong> {eventData.event_type || 'N/A'}</Typography>
                    <Typography><strong>Adverse Event:</strong> {eventData.adverse_event_flag || 'N/A'}</Typography>
                    <Typography><strong>Report To FDA:</strong> {eventData.report_to_fda || 'N/A'}</Typography>
                    <Typography><strong>Date of Event:</strong> {eventData.date_of_event || 'N/A'}</Typography>
                    <Typography><strong>Date Reported:</strong> {eventData.date_report || 'N/A'}</Typography>
                    <Typography><strong>Reporter Country:</strong> {eventData.reporter_country_code || 'N/A'}</Typography>

                    {eventData.device?.length ? (
                        <Box>
                            <Typography variant="h6">Device Information</Typography>
                            {eventData.device.map((dev, index) => (
                                <Box key={index} mb={1}>
                                    <Typography><strong>Brand:</strong> {dev.brand_name || 'N/A'}</Typography>
                                    <Typography><strong>Generic:</strong> {dev.generic_name || 'N/A'}</Typography>
                                    <Typography><strong>Manufacturer:</strong> {dev.manufacturer_d_name || 'N/A'}</Typography>
                                    <Typography><strong>Implant Flag:</strong> {dev.implant_flag || 'N/A'}</Typography>
                                    <Typography><strong>UDI:</strong> {dev.udi_public || 'N/A'}</Typography>
                                    <Typography><strong>OpenFDA Device Name:</strong> {dev.openfda?.device_name || 'N/A'}</Typography>
                                    <Typography><strong>Specialty:</strong> {dev.openfda?.medical_specialty_description || 'N/A'}</Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : null}

                    {Array.isArray(eventData.mdr_text) && eventData.mdr_text.length > 0 && (
                        <Box>
                            <Typography variant="h6">Narrative Text</Typography>
                            {eventData.mdr_text.map((item, idx) => (
                                <Typography key={idx} paragraph>
                                    {item.text_type_code}: {item.text}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );

};

export default FdaEventModal;
